import { useEffect, useState, useCallback, Fragment } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, X, CaretRight, CaretDown, ArrowClockwise, Plus, Trash, MagicWand } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";


const numf = (v) => { const n = parseFloat(String(v ?? "").replace(/,/g, "")); return Number.isFinite(n) ? n : 0; };
const inr = (v) => "₹" + numf(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const money = (v, cur) => {
  const n = numf(v);
  if ((cur || "").toUpperCase().includes("US")) return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if ((cur || "").toUpperCase().includes("POUND")) return "£" + n.toLocaleString("en-GB", { maximumFractionDigits: 2 });
  return "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

// Display dates as DD-MMM-YY (e.g. 03-Sep-26). Accepts ISO "YYYY-MM-DD…"; leaves other strings as-is.
const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtD = (v) => {
  if (!v) return "—";
  const m = String(v).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(v);
  return `${m[3]}-${_MON[+m[2] - 1]}-${m[1].slice(2)}`;
};

const th = { padding: "9px 10px", fontSize: 11, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", textAlign: "left" };
const td = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap" };

const STATUS = {
  settled: { bg: "#DCFCE7", fg: "#166534", label: "SETTLED" },
  partial: { bg: "#FEF3C7", fg: "#92400E", label: "PARTIAL" },
  open: { bg: "#FEE2E2", fg: "#991B1B", label: "OPEN" },
};

const StatusBadge = ({ s }) => {
  const c = STATUS[s] || STATUS.open;
  return <span data-testid={`fa-status-${s}`} style={{ display: "inline-block", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: c.bg, color: c.fg }}>{c.label}</span>;
};

const TypeBadge = ({ exp, dbn }) => {
  if (dbn) return (
    <span style={{ display: "inline-block", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: "#FEF9C3", color: "#854D0E" }}>
      DEBIT NOTE
    </span>
  );
  return (
    <span style={{ display: "inline-block", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: exp ? "#E0F2FE" : "#F1F5F9", color: exp ? "#075985" : "#475569" }}>
      {exp ? "EXPORT" : "DOMESTIC"}
    </span>
  );
};

const Tile = ({ label, value, sub, color, active, onClick, testid }) => {
  // Scale the value font down for long amounts (e.g. ₹89,69,67,398.28) so it never overflows the tile.
  const vlen = String(value ?? "").length;
  const valFont = vlen <= 8 ? 19 : vlen <= 11 ? 16 : vlen <= 14 ? 14 : vlen <= 17 ? 12.5 : 11;
  return (
    <button data-testid={testid} onClick={onClick} disabled={!onClick}
      style={{ textAlign: "left", flex: "1 1 150px", minWidth: 140, maxWidth: "100%", overflow: "hidden", background: "#fff", border: `1px solid ${active ? color : "#E2E8F0"}`, borderRadius: 12, padding: "12px 14px", cursor: onClick ? "pointer" : "default", boxShadow: active ? `0 0 0 2px ${color}22` : "none", transition: "border-color .15s, box-shadow .15s" }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: .3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
      <div title={String(value ?? "")} style={{ fontSize: valFont, fontWeight: 800, color, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "#94A3B8", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
    </button>
  );
};

// Per-customer trial-balance breakdown behind the Total Outstanding (INR) tile.
function OutstandingModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ net_inr: 0, overdue_inr: 0, count: 0 });
  const [plant, setPlant] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/fa-logistics/outstanding-breakdown");
        if (!alive) return;
        setRows(r.data?.rows || []); setTotals(r.data?.totals || { net_inr: 0, overdue_inr: 0, count: 0 });
      } catch (e) { if (alive) toast.error(e.response?.data?.detail || "Failed to load breakdown"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const plants = Array.from(new Set(rows.map((r) => r.plant).filter(Boolean))).sort();
  const shown = rows.filter((r) => (plant === "all" || r.plant === plant) && (!overdueOnly || Math.abs(r.overdue_inr) > 0.5));
  const sumNet = shown.reduce((s, r) => s + r.net_receivable_inr, 0);
  const sumOd = shown.reduce((s, r) => s + r.overdue_inr, 0);
  const mth = { textAlign: "left", padding: "8px 10px", fontSize: 10.5, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: .3, background: "#F1F5F9", position: "sticky", top: 0, borderBottom: "1px solid #E2E8F0" };
  const mtd = { padding: "7px 10px", fontSize: 12.5, color: "#0F172A", borderTop: "1px solid #F1F5F9" };

  return (
    <div data-testid="fa-outstanding-modal" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 1000, display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "min(1080px, 97vw)", maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(2,8,23,.35)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #E2E8F0" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0F172A" }}>Total Outstanding (INR) — Customer Trial Balance</div>
            <div style={{ fontSize: 12, color: "#64748B" }}>{totals.count} customers · Net {inr(totals.net_inr)} · Overdue {inr(totals.overdue_inr)}</div>
          </div>
          <button data-testid="fa-outstanding-close" onClick={onClose} style={{ display: "grid", placeItems: "center", width: 32, height: 32, borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff", cursor: "pointer" }}><X size={16} weight="bold" /></button>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "10px 20px", borderBottom: "1px solid #F1F5F9", background: "#F8FAFC", flexWrap: "wrap" }}>
          <label style={{ fontSize: 12.5, color: "#475569", fontWeight: 600, display: "flex", gap: 6, alignItems: "center" }}>Plant
            <select data-testid="fa-outstanding-plant" value={plant} onChange={(e) => setPlant(e.target.value)} style={{ padding: "5px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 12.5 }}>
              <option value="all">All</option>
              {plants.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12.5, color: "#475569", fontWeight: 600, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input data-testid="fa-outstanding-overdue" type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} /> Overdue only
          </label>
          <div style={{ marginLeft: "auto", fontSize: 12.5, color: "#0F172A", fontWeight: 700 }}>Net {inr(sumNet)} · <span style={{ color: "#DC2626" }}>Overdue {inr(sumOd)}</span></div>
        </div>
        <div style={{ overflow: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead><tr>
              <th style={mth}>Plant</th><th style={mth}>Cust Code</th><th style={mth}>Customer</th>
              <th style={{ ...mth, textAlign: "right" }}>Credit Days</th><th style={mth}>Ccy</th>
              <th style={{ ...mth, textAlign: "right" }}>Net Receivable</th>
              <th style={{ ...mth, textAlign: "right" }}>Net Rec (INR)</th>
              <th style={{ ...mth, textAlign: "right" }}>Overdue (INR)</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{ ...mtd, textAlign: "center", padding: 30, color: "#94A3B8" }}>Loading…</td></tr>}
              {!loading && shown.map((r, i) => (
                <tr key={i} data-testid={`fa-outstanding-row-${i}`}>
                  <td style={{ ...mtd, fontWeight: 700 }}>{r.plant}</td>
                  <td style={mtd}>{r.cust_code}</td>
                  <td style={{ ...mtd, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.customer}>{r.customer}</td>
                  <td style={{ ...mtd, textAlign: "right" }}>{r.credit_days}</td>
                  <td style={mtd}>{r.currency}</td>
                  <td style={{ ...mtd, textAlign: "right" }}>{money(r.net_receivable_fc, r.currency)}</td>
                  <td style={{ ...mtd, textAlign: "right", fontWeight: 700 }}>{inr(r.net_receivable_inr)}</td>
                  <td style={{ ...mtd, textAlign: "right", color: Math.abs(r.overdue_inr) > 0.5 ? "#DC2626" : "#94A3B8" }}>{inr(r.overdue_inr)}</td>
                </tr>
              ))}
              {!loading && shown.length === 0 && <tr><td colSpan={8} style={{ ...mtd, textAlign: "center", padding: 30, color: "#94A3B8" }}>No customers match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function ReceiptTimeline({ saleNo, voc, compcode, pcode, saleNoc, reloadKey, onChanged, onRowRefreshed }) {
  const [recs, setRecs] = useState(null);
  const fetchRecs = useCallback(() => {
    let alive = true;
    let u = `/fa-logistics/receipts?sale_no=${encodeURIComponent(saleNo)}`;
    if (voc === "DB Note" || voc === "DBN") u += `&voc=${encodeURIComponent(voc)}&compcode=${encodeURIComponent(compcode || "")}`
      + `&pcode=${encodeURIComponent(pcode || "")}&sale_noc=${encodeURIComponent(saleNoc || "")}`;
    api.get(u)
      .then((r) => { if (alive) { setRecs(r.data?.receipts || []); if (r.data?.row) onRowRefreshed && onRowRefreshed(r.data.row); } })
      .catch(() => { if (alive) setRecs([]); });
    return () => { alive = false; };
  }, [saleNo, voc, compcode, pcode, saleNoc, onRowRefreshed]);
  useEffect(() => fetchRecs(), [fetchRecs, reloadKey]);

  const delStaged = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this staged receipt?")) return;
    try { await api.delete(`/fa-logistics/receipts/${id}`); toast.success("Staged receipt removed"); fetchRecs(); onChanged && onChanged(); }
    catch { toast.error("Failed to remove"); }
  };

  const [posting, setPosting] = useState("");
  const postStaged = async (batchId, e) => {
    e.stopPropagation();
    if (!batchId) { toast.error("This staged receipt predates batch tracking — please remove and re-enter it."); return; }
    if (!window.confirm("Post this staged receipt into the ERP now? This creates a receipt voucher.")) return;
    setPosting(batchId);
    try {
      const res = await api.post(`/fa-logistics/receipts/post-staged/${batchId}`);
      toast.success(`Posted to ERP — Voucher #${res.data?.voc_no}`);
      fetchRecs(); onChanged && onChanged();
    } catch (e2) { toast.error(e2.response?.data?.detail || "ERP post failed"); }
    finally { setPosting(""); }
  };

  if (recs === null) return <div style={{ padding: "10px 16px", fontSize: 12, color: "#94A3B8" }}>Loading receipts…</div>;
  if (recs.length === 0) return <div data-testid="fa-no-receipts" style={{ padding: "10px 16px", fontSize: 12, color: "#94A3B8" }}>No receipts recorded yet — this invoice is still open.</div>;
  return (
    <div style={{ padding: "8px 16px 12px 48px" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8 }} data-testid="fa-receipt-table">
        <thead>
          <tr style={{ background: "#F8FAFC" }}>
            {["Source", "Date", "Receipt Type", "Amount Received", "Exchange Rate", "INR Amount", "Exchange Gain / Loss", "Shipping Bill No", "BL No", "Remark", ""].map((h) => (
              <th key={h} style={{ ...th, color: "#475569", fontSize: 10.5, borderBottom: "1px solid #E2E8F0" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {recs.map((x, i) => {
            const d = recDisplay(x);
            return (
            <tr key={x.id || i} style={{ background: x.source === "staged" ? "#FFFBEB" : "#fff" }}>
              <td style={td}><span style={{ fontSize: 9.5, fontWeight: 800, padding: "1px 6px", borderRadius: 999, background: x.source === "staged" ? "#FEF3C7" : "#E0F2FE", color: x.source === "staged" ? "#92400E" : "#075985" }}>{x.source === "staged" ? "PENDING" : "ERP"}</span></td>
              <td style={td}>{fmtD(x.date)}</td>
              <td style={td}><span style={{ fontWeight: 700, color: x.rec_type === "Bank Charges" ? "#B45309" : "#0F172A" }}>{d.label}</span></td>
              <td style={{ ...td, textAlign: "right" }}>{d.amt.toLocaleString("en-US", { maximumFractionDigits: 2 })}</td>
              <td style={{ ...td, textAlign: "right" }}>{d.isIncentive ? "1.00" : (x.exch_rate || "—")}</td>
              <td style={{ ...td, textAlign: "right" }}>{inr(d.inrAmt)}</td>
              <td style={{ ...td, textAlign: "right", fontWeight: 700, color: numf(x.gain_loss_inr) >= 0 ? "#16A34A" : "#DC2626" }}>{inr(x.gain_loss_inr)}</td>
              <td style={td}>{x.sb_no || "—"}</td>
              <td style={td}>{x.bl_no || "—"}</td>
              <td style={{ ...td, whiteSpace: "normal", maxWidth: 220 }}>{x.remark || "—"}</td>
              <td style={td}>{x.source === "staged" && (
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button data-testid={`fa-post-staged-${x.id}`} onClick={(e) => postStaged(x.batch_id, e)} disabled={posting === x.batch_id} style={{ border: "1px solid #0EA5E9", background: "#E0F2FE", cursor: "pointer", color: "#075985", fontWeight: 700, fontSize: 11, borderRadius: 6, padding: "2px 8px" }}>{posting === x.batch_id ? "Posting…" : "Post to ERP"}</button>
                  <button data-testid={`fa-del-staged-${x.id}`} onClick={(e) => delStaged(x.id, e)} style={{ border: "none", background: "none", cursor: "pointer", color: "#DC2626", fontWeight: 700, fontSize: 11 }}>Remove</button>
                </div>
              )}</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// A Drawback / GST / RODTEP receipt carries its value in a dedicated INR column
// (drawback / gst_amt / incentive) with cur_amt = 0 — the ERP files all three under RecType
// "Others". Surface that INR amount (and a clearer label) in the receipt timeline so incentive
// receipts (whether bulk-uploaded or entered manually) don't read as ₹0. Regular customer/bank
// receipts are unchanged (shown in their own currency at their exchange rate).
function recDisplay(x) {
  const dbk = numf(x.drawback), gst = numf(x.gst_amt), incen = numf(x.incentive);
  const incentive = dbk || gst || incen;
  const isIncentive = numf(x.cur_amt) === 0 && incentive !== 0;
  let label = x.rec_type || "—";
  if (isIncentive && (x.rec_type === "Others" || !x.rec_type)) {
    label = dbk ? "Drawback (DBK)" : gst ? "GST Refund" : "RODTEP";
  }
  return {
    isIncentive,
    label,
    amt: isIncentive ? incentive : numf(x.cur_amt),
    inrAmt: isIncentive ? incentive : numf(x.inr_amt),
  };
}

const REC_TYPE_LABELS = {
  "Customer": "From Customer", "Bank Charges": "Bank Charges", "DBK Rec": "Drawback Receipt",
  "Gst Rec": "GST Receipt", "RODTEP Rec": "RODTEP Receipt",
  "Adjustment": "Adjustment", "INR Adjustment": "INR Adjustment (legacy)",
};
// Receipt types that count as "money received against the invoice value" (mirrors backend
// _RECEIVED_TYPES). Includes both the app label ("Customer") and the ERP label ("From Customer").
// "Adjustment" is a small-residual write-off in the invoice currency — it nets off like Bank Charges.
const RECEIVED_TYPES = new Set(["Customer", "From Customer", "Bank Charges", "Adjustment"]);
// The receipt types whose secondary INR amount field is relevant (revealed inside the row's details).
const TYPE_AMT = { "Gst Rec": ["gst_amt", "GST Amount (INR)", "gst"], "DBK Rec": ["dbk_amt", "Drawback Amount (INR)", "dbk"], "RODTEP Rec": ["incen_amt", "RODTEP Amount (INR)", "incen"] };
const blankLine = (exch = "", voc = "Bill") => ({ rec_type: "Customer", rec_date: new Date().toISOString().slice(0, 10), cur_amt: "", exch_rate: exch, gst_amt: "", dbk_amt: "", incen_amt: "", voc, remark: "" });

function _actBtn(color, bg) {
  return { fontSize: 11, fontWeight: 700, padding: "3px 9px", marginLeft: 6, borderRadius: 6,
           border: "1px solid " + bg, background: bg, color, cursor: "pointer" };
}

// ERP RecType vocabulary -> app receipt-type (for reversing/correcting a posted receipt).
const ERP_TO_APP = { "From Customer": "Customer", "Bank Charges": "Bank Charges",
                     "Adjustment": "Adjustment", "INR Adjustment": "INR Adjustment", "Others": "Gst Rec" };

function EditReceiptDialog({ row, inv, recTypes, onClose, onDone }) {
  const isStaged = row.source === "staged";
  const [recType, setRecType] = useState(isStaged ? (row.rec_type || "Customer") : (ERP_TO_APP[row.rec_type] || "Gst Rec"));
  const [amount, setAmount] = useState(String(numf(row.cur_amt) || ""));
  const [rate, setRate] = useState(String(numf(row.exch_rate) || numf(inv?.bill_exch_rate) || ""));
  const [voidMode, setVoidMode] = useState(!!row._void);
  const [busy, setBusy] = useState(false);
  const types = (recTypes && recTypes.length ? recTypes : ["Customer", "Bank Charges", "DBK Rec", "Gst Rec", "RODTEP Rec", "Adjustment"]);
  const newInr = numf(amount) * numf(rate);
  const origInr = numf(row.cur_amt) * numf(row.exch_rate);

  const save = async () => {
    setBusy(true);
    try {
      if (isStaged) {
        await api.patch(`/fa-logistics/receipts/${row.id}`, {
          rec_type: recType, rec_date: (row.date || row.rec_date || ""), cur_amt: numf(amount),
          exch_rate: numf(rate), gst_amt: numf(row.gst_amt), dbk_amt: numf(row.drawback),
          incen_amt: numf(row.incentive), voc: row.voc || "Bill", remark: row.remark || "",
        });
        toast.success("Pending receipt updated");
      } else {
        const msg = voidMode
          ? `Void receipt #${row.voc_no}? This posts a REVERSING voucher in the ERP (the original stays for audit).`
          : `Post a correction for receipt #${row.voc_no}? This posts an adjusting voucher in the ERP: reverse ${money(numf(row.cur_amt), inv.currency)} and book ${money(numf(amount), inv.currency)}.`;
        if (!window.confirm(msg)) { setBusy(false); return; }
        await api.post("/fa-logistics/receipts/correct", {
          sale_no: inv.sale_no, invoice_no: inv.invoice_no, export_invoice_no: inv.export_invoice_no,
          party: inv.party, currency: inv.currency, bill_exch_rate: numf(inv.bill_exch_rate),
          orig_voc_no: String(row.voc_no || ""),
          original: { rec_type: ERP_TO_APP[row.rec_type] || "Gst Rec", cur_amt: numf(row.cur_amt),
                      exch_rate: numf(row.exch_rate), gst_amt: numf(row.gst_amt),
                      dbk_amt: numf(row.drawback), incen_amt: numf(row.incentive) },
          corrected: voidMode ? null : { rec_type: recType, cur_amt: numf(amount), exch_rate: numf(rate) },
        });
        toast.success(voidMode ? "Reversal posted to ERP" : "Correction posted to ERP");
      }
      onDone && onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const title = isStaged ? "Edit pending receipt" : (voidMode ? `Void ERP receipt #${row.voc_no}` : `Correct ERP receipt #${row.voc_no}`);
  const fld = { width: "100%", padding: "7px 9px", border: "1px solid #CBD5E1", borderRadius: 7, fontSize: 13, boxSizing: "border-box" };
  const lb = { fontSize: 10.5, fontWeight: 800, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 };
  return (
    <div data-testid="fa-edit-receipt-dialog" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 60, display: "grid", placeItems: "center", padding: 20 }}>
      <div onClick={(ev) => ev.stopPropagation()} style={{ width: 460, maxWidth: "94vw", background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.35)", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #E2E8F0" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0F172A" }}>{title}</div>
          <button data-testid="fa-edit-close" onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", color: "#94A3B8" }}><X size={18} weight="bold" /></button>
        </div>
        <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
          {!isStaged && (
            <div style={{ fontSize: 12, color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 10px" }}>
              The ERP has no edit/delete for a posted receipt, so this books an <b>adjusting voucher</b>: a reversal of {money(numf(row.cur_amt), inv.currency)} (₹{inr(origInr)}){voidMode ? "" : ` plus the corrected amount`}. The original stays visible for audit.
            </div>
          )}
          {!isStaged && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
              <input type="checkbox" data-testid="fa-edit-voidmode" checked={voidMode} onChange={(ev) => setVoidMode(ev.target.checked)} /> Void only (reverse, no replacement)
            </label>
          )}
          {!voidMode && (
            <>
              <div><div style={lb}>Receipt Type</div>
                <select data-testid="fa-edit-type" value={recType} onChange={(ev) => setRecType(ev.target.value)} style={fld}>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div><div style={lb}>Amount ({inv.currency})</div>
                  <input data-testid="fa-edit-amount" type="number" step="0.01" value={amount} onChange={(ev) => setAmount(ev.target.value)} style={fld} /></div>
                <div><div style={lb}>Rate</div>
                  <input data-testid="fa-edit-rate" type="number" step="0.0001" value={rate} onChange={(ev) => setRate(ev.target.value)} style={fld} /></div>
              </div>
              <div style={{ fontSize: 12, color: "#475569" }}>INR = <b>₹{inr(newInr)}</b>{!isStaged && <> · net change vs original = <b>₹{inr(newInr - origInr)}</b></>}</div>
            </>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "12px 18px", borderTop: "1px solid #E2E8F0", background: "#F8FAFC" }}>
          <button onClick={onClose} disabled={busy} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #CBD5E1", background: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>Cancel</button>
          <button data-testid="fa-edit-save" onClick={save} disabled={busy} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: voidMode ? "#B91C1C" : "#0369A1", color: "#fff", fontWeight: 800, fontSize: 12.5, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Saving…" : (isStaged ? "Save changes" : (voidMode ? "Post reversal" : "Post correction"))}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReceiptForm({ onClose, onSaved, initialInvoice, onInvoiceRefreshed }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [inv, setInv] = useState(null);          // selected invoice context
  const [opts, setOpts] = useState({ rec_types: [], voc_types: [] });
  const [lines, setLines] = useState([blankLine()]);
  const [blNo, setBlNo] = useState("");          // invoice-level BL (Bill of Lading) number
  const [blDate, setBlDate] = useState("");       // invoice-level BL date
  const [sbNo, setSbNo] = useState("");           // invoice-level Shipping Bill (customs) number
  const [sbDate, setSbDate] = useState("");       // invoice-level Shipping Bill date
  const [existing, setExisting] = useState(null); // ERP + staged receipts for the selected invoice
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [openDetail, setOpenDetail] = useState(null); // index of row whose details popover is open
  const [sbAuto, setSbAuto] = useState(""); // note shown when SB/BL was auto-filled from an uploaded shipping bill
  const [editing, setEditing] = useState(null); // an "Already recorded" receipt being edited/corrected
  const [sapMatch, setSapMatch] = useState(null);   // SAP GST-invoice dry-run resolver result
  const [sapLoading, setSapLoading] = useState(false);

  const receiptsUrl = (iv) => {
    let u = `/fa-logistics/receipts?sale_no=${encodeURIComponent(iv.sale_no)}`;
    if (iv.voc === "DB Note" || iv.voc === "DBN") {
      u += `&voc=${encodeURIComponent(iv.voc)}&compcode=${encodeURIComponent(iv.compcode || "")}`
         + `&pcode=${encodeURIComponent(iv.pcode || "")}&sale_noc=${encodeURIComponent(iv.sale_noc || "")}`;
    }
    return u;
  };

  const refreshExisting = async () => {
    if (!inv?.sale_no) return;
    try { const r = await api.get(receiptsUrl(inv)); setExisting(r.data?.receipts || []); if (r.data?.row) onInvoiceRefreshed && onInvoiceRefreshed(r.data.row); }
    catch { /* keep current */ }
  };
  const deleteStaged = async (id) => {
    if (!window.confirm("Remove this pending receipt? (It hasn't been posted to the ERP.)")) return;
    try { await api.delete(`/fa-logistics/receipts/${id}`); toast.success("Pending receipt removed"); refreshExisting(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  useEffect(() => { api.get("/fa-logistics/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  // Auto-fill the customs Shipping Bill (and BL, if the uploaded record carries one) for the picked
  // invoice — the link is the Export Invoice # (preferred) or the GST invoice #, from the shipping_bills
  // window. User can still override any auto-filled value before saving.
  const autofillShippingBill = async (iv) => {
    setSbAuto("");
    try {
      const r = await api.get(`/fa-logistics/shipping-bill-lookup?export_invoice_no=${encodeURIComponent(iv.export_invoice_no || "")}&gst_invoice_no=${encodeURIComponent(iv.invoice_no || "")}`);
      const d = r.data || {};
      if (!d.found) return;
      const parts = [];
      if (d.shipping_bill_no) { setSbNo(d.shipping_bill_no); parts.push("SB #"); }
      if (d.shipping_bill_date) setSbDate(d.shipping_bill_date);
      if (d.bl_no) { setBlNo(d.bl_no); parts.push(d.bl_source === "container" ? "BL # (from ERP)" : "BL #"); }
      if (d.bl_date) setBlDate(d.bl_date);
      if (parts.length) {
        const src = (parts.length === 1 && d.bl_source === "container" && !d.shipping_bill_no)
          ? "the ERP shipping record"
          : `uploaded Shipping Bill (matched by ${d.matched_by === "gst_invoice_number" ? "GST invoice #" : "Export invoice #"})`;
        setSbAuto(`Auto-filled ${parts.join(" + ")} from ${src}. You can override.`);
      }
    } catch { /* silent — auto-fill is best-effort */ }
  };

  const search = async () => {
    if (q.trim().length < 2) return;
    setSearching(true);
    try { const r = await api.get(`/fa-logistics/invoice-lookup?q=${encodeURIComponent(q.trim())}`); setResults(r.data?.invoices || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Lookup failed"); }
    finally { setSearching(false); }
  };

  const pickInvoice = async (iv) => {
    setInv(iv); setResults([]); setOpenDetail(null); setBlNo(""); setBlDate(""); setSbNo(""); setSbDate(""); setSbAuto("");
    setLines([blankLine(iv.bill_exch_rate ? String(iv.bill_exch_rate) : "", iv.voc || "Bill")]);
    setExisting(null);
    setSapMatch(null);
    if (iv.voc !== "DB Note" && iv.voc !== "DBN") { autofillShippingBill(iv); fetchSapMatch(iv); }
    try {
      const r = await api.get(receiptsUrl(iv));
      setExisting(r.data?.receipts || []);
      if (r.data?.row) onInvoiceRefreshed && onInvoiceRefreshed(r.data.row);
    } catch { setExisting([]); }
  };

  // READ-ONLY dry run: resolve this invoice's SAP ByDesign GST customer invoice + reconcile value.
  const fetchSapMatch = async (iv) => {
    if (!iv?.sale_no) return;
    setSapLoading(true);
    try {
      const r = await api.get(`/fa-logistics/sap-clearing-match?sale_no=${encodeURIComponent(iv.sale_no)}`);
      setSapMatch(r.data || null);
    } catch (e) {
      setSapMatch({ sap_found: false, flags: [e.response?.data?.detail || "SAP lookup failed"] });
    } finally { setSapLoading(false); }
  };

  // Pre-select the invoice when the form is opened directly from a table row.
  useEffect(() => { if (initialInvoice && initialInvoice.sale_no) pickInvoice(initialInvoice); /* eslint-disable-next-line */ }, []);

  const changeInvoice = () => { setInv(null); setExisting(null); setLines([blankLine()]); setOpenDetail(null); setBlNo(""); setBlDate(""); setSbNo(""); setSbDate(""); setSbAuto(""); };

  const setLine = (i, k, v) => setLines((p) => p.map((ln, idx) => (idx === i ? { ...ln, [k]: v } : ln)));
  // Guard: reject negative values in numeric fields (block at entry).
  const setNum = (i, k, v) => { if (v !== "" && parseFloat(v) < 0) return; setLine(i, k, v); };
  const blockNeg = (e) => { if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault(); };
  const addLine = () => setLines((p) => [...p, blankLine(inv?.bill_exch_rate ? String(inv.bill_exch_rate) : "")]);
  const removeLine = (i) => setLines((p) => (p.length <= 1 ? p : p.filter((_, idx) => idx !== i)));

  const bill = numf(inv?.bill_exch_rate);
  const valueFc = numf(inv?.value_fc);
  // DBK / RODTEP / GST receipts are INR government incentives captured at invoice level (they feed the
  // Shipping-Bill realized dues), NOT a foreign-currency customer payment — so their amount is entered
  // in ₹ with no exchange rate / gain-loss and routes into its dedicated column.
  const amtKeyFor = (ln) => (TYPE_AMT[ln.rec_type] ? TYPE_AMT[ln.rec_type][0] : "cur_amt");
  const hasDedicated = (ln) => !!TYPE_AMT[ln.rec_type];         // DBK / RODTEP / GST → own column
  const isInrType = (ln) => hasDedicated(ln) || ln.rec_type === "INR Adjustment"; // entered in ₹, no rate/gain
  const lineHasAmt = (ln) => numf(ln[amtKeyFor(ln)]) > 0;
  const lineInr = (ln) => (isInrType(ln) ? numf(ln[amtKeyFor(ln)]) : numf(ln.cur_amt) * numf(ln.exch_rate));
  const lineGain = (ln) => (isInrType(ln) ? 0 : numf(ln.cur_amt) * (numf(ln.exch_rate) - bill));
  // Received to date = "From Customer" + "Bank Charges" already on the invoice (ERP + previously staged).
  const receivedToDate = (existing || []).reduce((s, e) => s + (RECEIVED_TYPES.has(e.rec_type) ? numf(e.cur_amt) : 0), 0);
  const outstandingBefore = valueFc - receivedToDate;
  // This batch's contribution toward the invoice value (only real customer/bank cash receipts).
  const batchReceived = lines.reduce((s, ln) => s + (RECEIVED_TYPES.has(ln.rec_type) ? numf(ln.cur_amt) : 0), 0);
  const outstandingAfter = outstandingBefore - batchReceived;
  const batchInr = lines.reduce((s, ln) => s + lineInr(ln), 0);
  const batchGain = lines.reduce((s, ln) => s + lineGain(ln), 0);

  // One-click residual write-off: an "Adjustment" (a few cents/pence/paisa) is only meant to clear a
  // tiny leftover, so it is capped at 1.00 in the invoice currency. The button appears only when the
  // remaining dues after the currently-entered lines is a positive residual within that cap.
  const ADJ_MAX = 1.0;
  const residualNow = Math.round(outstandingAfter * 100) / 100;
  const canWriteOff = !!inv && residualNow > 0.001 && residualNow <= ADJ_MAX + 1e-9;
  const writeOffResidual = () => {
    if (!canWriteOff) return;
    setLines((p) => [...p, {
      ...blankLine(inv?.bill_exch_rate ? String(inv.bill_exch_rate) : "1"),
      rec_type: "Adjustment", cur_amt: String(residualNow),
      exch_rate: String(numf(inv?.bill_exch_rate) || 1),
      remark: "Residual write-off (auto)",
    }]);
  };

  const save = async () => {
    if (!inv) { toast.error("Pick an invoice first"); return; }
    if (lines.some((ln) => numf(ln.cur_amt) < 0 || numf(ln.exch_rate) < 0 || numf(ln.gst_amt) < 0 || numf(ln.dbk_amt) < 0 || numf(ln.incen_amt) < 0)) {
      toast.error("Negative amounts are not allowed"); return;
    }
    const clean = lines.filter(lineHasAmt);
    if (clean.length === 0) { toast.error("Enter at least one receipt line with an amount"); return; }
    if (clean.some((ln) => ln.rec_type === "Adjustment" && numf(ln.cur_amt) > ADJ_MAX + 1e-9)) {
      toast.error("An Adjustment can be at most 1.00 — it's a residual write-off only"); return;
    }
    if (clean.some((ln) => !isInrType(ln) && numf(ln.exch_rate) <= 0)) { toast.error("Every customer/bank receipt line needs an exchange rate"); return; }
    if (!window.confirm(`Post ${clean.length} receipt line${clean.length > 1 ? "s" : ""} to the ERP for invoice ${inv.invoice_no || inv.export_invoice_no}?\n\nThis creates a receipt voucher in the ERP and cannot be undone from here.`)) return;
    setSaving(true);
    try {
      const res = await api.post("/fa-logistics/receipts/post", {
        sale_no: inv.sale_no, invoice_no: inv.invoice_no, export_invoice_no: inv.export_invoice_no,
        pcode: inv.pcode, compcode: inv.compcode, party: inv.party, currency: inv.currency,
        sale_date: inv.sale_date, bill_exch_rate: bill,
        voc: inv.voc || "Bill", sale_noc: inv.sale_noc || "",
        lines: clean.map((ln) => ({
          rec_type: ln.rec_type, rec_date: ln.rec_date,
          cur_amt: isInrType(ln) ? 0 : numf(ln.cur_amt),
          exch_rate: isInrType(ln) ? 1 : numf(ln.exch_rate),
          gst_amt: numf(ln.gst_amt), dbk_amt: numf(ln.dbk_amt), incen_amt: numf(ln.incen_amt),
          sb_no: sbNo, sb_date: sbDate, bl_no: blNo, bl_date: blDate, voc: inv.voc || ln.voc, remark: ln.remark,
        })),
      });
      const voc = res.data?.voc_no;
      toast.success(`Posted to ERP — Voucher #${voc} (${clean.length} line${clean.length > 1 ? "s" : ""})`);
      onSaved && onSaved(inv.sale_no, inv.invoice_no);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "ERP post failed"); }
    finally { setSaving(false); }
  };

  const lbl = { fontSize: 10.5, fontWeight: 700, color: "#64748B", display: "block", marginBottom: 3 };
  const input = { width: "100%", padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 7, fontSize: 13 };
  const ctx = { fontSize: 12.5, fontWeight: 700, color: "#0F172A" };
  const gtd = { padding: "6px 8px", verticalAlign: "middle", whiteSpace: "nowrap" };
  const gsel = { width: "100%", minWidth: 150, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 12.5 };
  const gnum = { width: 100, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 12.5, textAlign: "right" };
  const gth = { padding: "8px 8px", fontSize: 10, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: .3, borderBottom: "1px solid #E2E8F0", background: "#F1F5F9", position: "sticky", top: 0 };
  const statTile = (label, value, color, testid, labelColor) => (
    <div style={{ flex: "1 1 120px", minWidth: 110 }}>
      <div style={{ ...lbl, color: labelColor || lbl.color }}>{label}</div>
      <div data-testid={testid} style={{ fontSize: 15, fontWeight: 800, color }}>{value}</div>
    </div>
  );

  return (
    <div data-testid="fa-receipt-form-overlay" onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 50, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="fa-receipt-form" style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 1040, boxShadow: "0 20px 60px rgba(15,23,42,.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid #E2E8F0" }}>
          <div style={{ fontSize: 16, fontWeight: 800 }}>Record Customer Receipts</div>
          <button data-testid="fa-form-close" onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", color: "#94A3B8" }}><X size={18} weight="bold" /></button>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Invoice picker */}
          <div>
            <label style={lbl}>Find Invoice (GST invoice #, export invoice # or customer)</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="fa-form-invsearch" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} placeholder="Type and press Enter…" style={{ ...input, flex: 1 }} />
              <button data-testid="fa-form-search-btn" onClick={search} disabled={searching} style={{ padding: "8px 16px", border: "none", borderRadius: 7, background: "#0EA5E9", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>{searching ? "…" : "Search"}</button>
            </div>
            {results.length > 0 && !inv && (
              <div style={{ marginTop: 6, maxHeight: 180, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
                {results.map((iv) => {
                  const isDbn = iv.voc === "DB Note" || iv.voc === "DBN";
                  return (
                    <button key={`${iv.voc}-${iv.compcode}-${iv.sale_no}`} data-testid={`fa-form-pick-${isDbn ? "dbn-" : ""}${iv.invoice_no}`} onClick={() => pickInvoice(iv)} style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", border: "none", borderBottom: "1px solid #F1F5F9", background: "#fff", cursor: "pointer", fontSize: 12.5 }}>
                      <span style={{ display: "inline-block", fontSize: 9.5, fontWeight: 800, letterSpacing: .4, padding: "1px 6px", borderRadius: 4, marginRight: 8, color: isDbn ? "#9A3412" : "#1E40AF", background: isDbn ? "#FFEDD5" : "#DBEAFE" }}>{isDbn ? "DEBIT NOTE" : "INVOICE"}</span>
                      <strong>{iv.invoice_no}</strong>{iv.export_invoice_no ? ` · exp ${iv.export_invoice_no}` : ""} · {iv.party} · {isDbn ? "Domestic" : (iv.is_export ? "Export" : "Domestic")} · {money(iv.value_fc, iv.currency)}{isDbn && iv.outstanding_fc != null ? ` · Outstanding ${money(iv.outstanding_fc, "INR")}` : ""}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {inv && (() => {
            const isDbn = inv.voc === "DB Note" || inv.voc === "DBN";
            return (
            <>
              {/* Auto-fetched context (read-only) + invoice-level BL / Shipping Bill entry */}
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: 14, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px,1fr))", gap: 12 }}>
                <div><div style={lbl}>Document Type</div><div data-testid="fa-form-doc-type"><span style={{ display: "inline-block", fontSize: 10, fontWeight: 800, letterSpacing: .4, padding: "2px 8px", borderRadius: 5, color: isDbn ? "#9A3412" : "#1E40AF", background: isDbn ? "#FFEDD5" : "#DBEAFE" }}>{isDbn ? "DEBIT NOTE" : "SALE INVOICE"}</span></div></div>
                <div><div style={lbl}>{isDbn ? "Debit Note #" : "GST Invoice #"}</div><div style={ctx}>{inv.invoice_no || "—"}</div></div>
                {!isDbn && <div><div style={lbl}>Export Invoice #</div><div style={ctx}>{inv.export_invoice_no || "—"}</div></div>}
                <div><div style={lbl}>Customer</div><div style={ctx}>{inv.party}</div></div>
                <div><div style={lbl}>Sale Date</div><div style={ctx}>{fmtD(inv.sale_date)}</div></div>
                <div><div style={lbl}>Currency</div><div style={ctx}>{inv.currency}</div></div>
                {!isDbn && <div><div style={lbl}>Billing Exchange Rate</div><div style={ctx}>{inv.bill_exch_rate || "—"}</div></div>}
                {!isDbn && <div><label style={lbl}>Shipping Bill No <span style={{ color: "#94A3B8", fontWeight: 500 }}>(customs)</span></label><input data-testid="fa-form-sb-no" value={sbNo} onChange={(e) => setSbNo(e.target.value)} style={input} /></div>}
                {!isDbn && <div><label style={lbl}>Shipping Bill Date</label><input data-testid="fa-form-sb-date" type="date" value={sbDate} onChange={(e) => setSbDate(e.target.value)} style={input} /></div>}
                {!isDbn && <div><label style={lbl}>BL Number <span style={{ color: "#94A3B8", fontWeight: 500 }}>(Bill of Lading)</span></label><input data-testid="fa-form-bl-no" value={blNo} onChange={(e) => setBlNo(e.target.value)} style={input} /></div>}
                {!isDbn && <div><label style={lbl}>BL Date</label><input data-testid="fa-form-bl-date" type="date" value={blDate} onChange={(e) => setBlDate(e.target.value)} style={input} /></div>}
                <button data-testid="fa-form-change-invoice" onClick={changeInvoice} style={{ alignSelf: "end", border: "1px solid #CBD5E1", borderRadius: 7, background: "#fff", color: "#475569", fontWeight: 700, fontSize: 11.5, padding: "6px 10px", cursor: "pointer" }}>Change document</button>
              </div>
              {sbAuto && (
                <div data-testid="fa-form-sb-auto-note" style={{ display: "flex", alignItems: "center", gap: 6, marginTop: -8, fontSize: 11.5, color: "#0E7490", fontWeight: 600 }}>
                  <MagicWand size={13} weight="bold" /> {sbAuto}
                </div>
              )}

              {/* Live invoice status */}
              <div data-testid="fa-form-status" style={{ display: "flex", gap: 16, flexWrap: "wrap", padding: "12px 16px", background: "#0E7490", border: "1px solid #155E75", borderRadius: 10 }}>
                {statTile("Invoice Value", money(valueFc, inv.currency), "#FFFFFF", "fa-form-inv-value", "#FFFFFF")}
                {statTile("Received to Date", money(receivedToDate, inv.currency), receivedToDate > 0 ? "#6EE7B7" : "#FFFFFF", "fa-form-recd-todate", "#FFFFFF")}
                {statTile("Outstanding (Dues)", money(outstandingBefore, inv.currency), outstandingBefore > 0.01 ? "#F87171" : "#6EE7B7", "fa-form-dues", "#FFFFFF")}
                {existing === null && <div style={{ alignSelf: "center", fontSize: 11, color: "rgba(255,255,255,.85)" }}>loading receipts…</div>}
              </div>

              {/* SAP ByDesign GST invoice — READ-ONLY dry run (clearing bridge resolver) */}
              {inv.voc !== "DB Note" && inv.voc !== "DBN" && (sapLoading || sapMatch) && (
                <div data-testid="fa-form-sap-match" style={{ border: "1px solid #C7D2FE", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "8px 12px", background: sapMatch?.needs_receivables_review ? "#FEF2F2" : "#EEF2FF", borderBottom: `1px solid ${sapMatch?.needs_receivables_review ? "#FECACA" : "#C7D2FE"}`, fontSize: 11.5, fontWeight: 800, color: sapMatch?.needs_receivables_review ? "#991B1B" : "#3730A3", textTransform: "uppercase", letterSpacing: 0.4, display: "flex", alignItems: "center", gap: 8 }}>
                    SAP ByDesign GST Invoice <span style={{ fontWeight: 600, fontSize: 10, color: sapMatch?.needs_receivables_review ? "#DC2626" : "#6366F1" }}>· read-only dry run</span>
                    {sapMatch?.needs_receivables_review && <span data-testid="fa-form-sap-review-badge" style={{ marginLeft: "auto", fontSize: 9.5, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: "#DC2626", color: "#fff" }}>RECEIVABLES REVIEW</span>}
                  </div>
                  {sapLoading ? (
                    <div data-testid="fa-form-sap-loading" style={{ padding: "10px 12px", fontSize: 12, color: "#64748B" }}>Resolving SAP GST invoice…</div>
                  ) : sapMatch?.sap_found ? (
                    <div style={{ padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: 18, fontSize: 12.5 }}>
                      <div><div style={lbl}>SAP Invoice ID</div><div data-testid="fa-form-sap-id" style={{ fontWeight: 800, color: "#1E293B" }}>{sapMatch.sap_invoice_id}</div></div>
                      <div><div style={lbl}>SAP Date</div><div style={{ fontWeight: 700 }}>{fmtD(sapMatch.sap_date)}</div></div>
                      <div><div style={lbl}>{sapMatch.is_export ? "SAP Net (FOB)" : "SAP Gross (incl GST)"}</div><div data-testid="fa-form-sap-amt" style={{ fontWeight: 800 }}>{money(sapMatch.sap_customer_amount, sapMatch.sap_currency)}</div></div>
                      {sapMatch.sap_tax ? <div><div style={lbl}>SAP IGST</div><div style={{ fontWeight: 700, color: "#64748B" }}>{money(sapMatch.sap_tax, sapMatch.sap_currency)}</div></div> : null}
                      <div><div style={lbl}>Value check</div>
                        <div data-testid="fa-form-sap-valcheck" style={{ fontWeight: 800, color: (sapMatch.flags || []).some((f) => f.includes("value mismatch")) ? "#B91C1C" : "#15803D" }}>
                          {(sapMatch.flags || []).some((f) => f.includes("value mismatch")) ? "Mismatch" : "Matches app"}
                        </div>
                      </div>
                      {sapMatch.sap_export_no ? <div><div style={lbl}>SAP Export No</div><div data-testid="fa-form-sap-export-no" style={{ fontWeight: 700 }}>{sapMatch.sap_export_no}</div></div>
                        : (sapMatch.is_export && (sapMatch.export_stamp_status === "missing" || sapMatch.export_stamp_status === "app_export_no_missing")
                          ? <div><div style={lbl}>SAP Export No</div><div data-testid="fa-form-sap-export-missing" style={{ fontWeight: 800, color: "#B91C1C" }}>missing</div></div> : null)}
                      {sapMatch.flags && sapMatch.flags.length > 0 && (
                        <div data-testid="fa-form-sap-flags" style={{ flexBasis: "100%", display: "flex", flexDirection: "column", gap: 3, marginTop: 2 }}>
                          {sapMatch.flags.map((f, i) => {
                            const isReview = /receivables|mismatch/i.test(f);
                            return (
                              <div key={i} style={{ fontSize: 11.5, color: isReview ? "#991B1B" : "#92400E", background: isReview ? "#FEE2E2" : "#FEF3C7", borderRadius: 6, padding: "4px 8px", fontWeight: 600 }}>⚠ {f}</div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div data-testid="fa-form-sap-notfound" style={{ padding: "10px 12px", fontSize: 12, color: "#B45309" }}>
                      {sapMatch?.doc_id ? `No SAP GST invoice ${sapMatch.doc_id} found. ` : ""}
                      {(sapMatch?.flags || []).join(" · ") || "Not resolved."}
                    </div>
                  )}
                </div>
              )}

              {/* Receipts already recorded against this invoice (ERP + any local staging) */}
              {existing && existing.length > 0 && (
                <div data-testid="fa-form-existing" style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "8px 12px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0", fontSize: 11.5, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    Already recorded ({existing.length})
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th style={{ ...gth, textAlign: "left" }}>Date</th>
                        <th style={{ ...gth, textAlign: "left" }}>Type</th>
                        <th style={{ ...gth, textAlign: "right" }}>Amount</th>
                        <th style={{ ...gth, textAlign: "right" }}>Rate</th>
                        <th style={{ ...gth, textAlign: "right" }}>INR</th>
                        <th style={{ ...gth, textAlign: "left" }}>Voucher / Ref</th>
                        <th style={{ ...gth, textAlign: "right" }}>Source</th>
                        <th style={{ ...gth, textAlign: "right", width: 150 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {existing.map((e, i) => {
                        const d = recDisplay(e);
                        return (
                        <tr key={i} data-testid={`fa-form-existing-row-${i}`} style={{ borderTop: "1px solid #F1F5F9" }}>
                          <td style={gtd}>{fmtD(e.date || e.rec_date)}</td>
                          <td style={gtd}>{d.label}</td>
                          <td style={{ ...gtd, textAlign: "right", fontWeight: 700 }}>{money(d.amt, d.isIncentive ? "INR" : inv.currency)}</td>
                          <td style={{ ...gtd, textAlign: "right" }}>{d.isIncentive ? "1.00" : (numf(e.exch_rate) ? numf(e.exch_rate).toFixed(2) : "—")}</td>
                          <td style={{ ...gtd, textAlign: "right" }}>{inr(d.inrAmt)}</td>
                          <td style={gtd}>{e.voc_no ? `#${e.voc_no}` : (e.remark || "—")}</td>
                          <td style={{ ...gtd, textAlign: "right" }}>
                            <span style={{ fontSize: 9.5, fontWeight: 800, padding: "2px 7px", borderRadius: 999, color: e.source === "staged" ? "#92400E" : "#166534", background: e.source === "staged" ? "#FEF3C7" : "#DCFCE7" }}>{e.source === "staged" ? "PENDING" : "ERP"}</span>
                          </td>
                          <td style={{ ...gtd, textAlign: "right", whiteSpace: "nowrap" }}>
                            {e.source === "staged" ? (
                              <>
                                <button data-testid={`fa-edit-staged-${i}`} onClick={() => setEditing({ ...e, _i: i })} style={_actBtn("#0369A1", "#E0F2FE")}>Edit</button>
                                <button data-testid={`fa-del-staged-${i}`} onClick={() => deleteStaged(e.id)} style={_actBtn("#B91C1C", "#FEE2E2")}>Delete</button>
                              </>
                            ) : (
                              <>
                                <button data-testid={`fa-correct-erp-${i}`} onClick={() => setEditing({ ...e, _i: i, _void: false })} style={_actBtn("#0369A1", "#E0F2FE")}>Correct</button>
                                <button data-testid={`fa-void-erp-${i}`} onClick={() => setEditing({ ...e, _i: i, _void: true })} style={_actBtn("#B91C1C", "#FEE2E2")}>Void</button>
                              </>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {editing && (
                <EditReceiptDialog row={editing} inv={inv} recTypes={opts?.rec_types}
                  onClose={() => setEditing(null)} onDone={refreshExisting} />
              )}

              {/* Receipt lines — compact grid (one row per receipt; extra fields in a per-row details popover) */}
              <div data-testid="fa-form-lines" style={{ border: "1px solid #E2E8F0", borderRadius: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ ...gth, textAlign: "left", width: 28 }}>#</th>
                      <th style={{ ...gth, textAlign: "left" }}>Receipt Type</th>
                      <th style={{ ...gth, textAlign: "left" }}>Receipt Date</th>
                      <th style={{ ...gth, textAlign: "right" }}>Amount</th>
                      <th style={{ ...gth, textAlign: "right" }}>Rate</th>
                      <th style={{ ...gth, textAlign: "right" }}>INR</th>
                      <th style={{ ...gth, textAlign: "right" }}>Gain / Loss</th>
                      <th style={{ ...gth, textAlign: "left" }}>Bank Ref#</th>
                      <th style={{ ...gth, width: 40 }}></th>
                      <th style={{ ...gth, width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((ln, i) => {
                      const extra = TYPE_AMT[ln.rec_type];
                      const isInr = !!extra;
                      const amtKey = isInr ? extra[0] : "cur_amt";
                      const lnInr = lineInr(ln);
                      const lnGain = lineGain(ln);
                      const open = openDetail === i;
                      return (
                        <tr key={i} data-testid={`fa-form-line-${i}`} style={{ borderBottom: "1px solid #F1F5F9" }}>
                          <td style={{ ...gtd, color: "#94A3B8", fontWeight: 700, fontSize: 12 }}>{i + 1}</td>
                          <td style={gtd}>
                            <select data-testid={`fa-form-rectype-${i}`} value={ln.rec_type} onChange={(e) => setLine(i, "rec_type", e.target.value)} style={gsel}>
                              {(opts.rec_types || []).map((t) => <option key={t} value={t}>{REC_TYPE_LABELS[t] || t}</option>)}
                            </select>
                          </td>
                          <td style={gtd}><input data-testid={`fa-form-recdate-${i}`} type="date" value={ln.rec_date} onChange={(e) => setLine(i, "rec_date", e.target.value)} style={{ padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 12.5 }} /></td>
                          <td style={{ ...gtd, textAlign: "right" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: isInr ? "#0E7490" : "#64748B" }}>{isInr ? "₹" : (inv.currency || "").includes("US") ? "$" : "₹"}</span>
                              <input data-testid={`fa-form-curamt-${i}`} type="number" min="0" step="any" value={ln[amtKey]} onKeyDown={blockNeg} onChange={(e) => setNum(i, amtKey, e.target.value)} style={gnum} />
                            </div>
                          </td>
                          <td style={{ ...gtd, textAlign: "right" }}>
                            {isInr
                              ? <span data-testid={`fa-form-exchrate-${i}`} title="INR incentive — no conversion" style={{ fontSize: 12, color: "#94A3B8" }}>—</span>
                              : <input data-testid={`fa-form-exchrate-${i}`} type="number" min="0" step="any" value={ln.exch_rate} onKeyDown={blockNeg} onChange={(e) => setNum(i, "exch_rate", e.target.value)} style={{ ...gnum, width: 80 }} />}
                          </td>
                          <td style={{ ...gtd, textAlign: "right", fontSize: 12 }} data-testid={`fa-form-line-inr-${i}`}>{inr(lnInr)}</td>
                          <td style={{ ...gtd, textAlign: "right", fontSize: 12, fontWeight: 700, color: lnGain >= 0 ? "#16A34A" : "#DC2626" }} data-testid={`fa-form-line-gain-${i}`}>{isInr ? "—" : inr(lnGain)}</td>
                          <td style={gtd}><input data-testid={`fa-form-remark-${i}`} value={ln.remark} placeholder="UTR / Ref#" onChange={(e) => setLine(i, "remark", e.target.value)} style={{ width: 140, padding: "6px 8px", border: "1px solid #CBD5E1", borderRadius: 6, fontSize: 12.5 }} /></td>
                          <td style={{ ...gtd, position: "relative", textAlign: "center" }}>
                            <button data-testid={`fa-form-details-${i}`} onClick={() => setOpenDetail(open ? null : i)} title="More details (document type)" style={{ border: `1px solid ${open ? "#38BDF8" : "#CBD5E1"}`, background: open ? "#E0F2FE" : "#fff", borderRadius: 6, padding: "3px 9px", cursor: "pointer", color: "#475569", fontWeight: 800, fontSize: 13, lineHeight: 1 }}>⋯</button>
                            {open && (
                              <div data-testid={`fa-form-details-panel-${i}`} onClick={(e) => e.stopPropagation()} style={{ position: "absolute", right: 0, top: "100%", zIndex: 30, marginTop: 6, width: 280, background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, boxShadow: "0 12px 30px rgba(15,23,42,.18)", padding: 12, textAlign: "left", display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
                                {isInr && <div style={{ fontSize: 11.5, color: "#0E7490", fontWeight: 600 }}>{extra[1]} is entered in the Amount column (INR).</div>}
                                <div><label style={lbl}>Document Type</label>
                                  <select data-testid={`fa-form-voc-${i}`} value={ln.voc} onChange={(e) => setLine(i, "voc", e.target.value)} style={input}>
                                    {(opts.voc_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                                  </select>
                                </div>
                                <button data-testid={`fa-form-details-done-${i}`} onClick={() => setOpenDetail(null)} style={{ border: "none", background: "#0F172A", color: "#fff", borderRadius: 7, padding: "7px 0", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Done</button>
                              </div>
                            )}
                          </td>
                          <td style={{ ...gtd, textAlign: "center" }}>
                            {lines.length > 1 && (
                              <button data-testid={`fa-form-line-remove-${i}`} onClick={() => { removeLine(i); setOpenDetail(null); }} title="Remove line" style={{ border: "none", background: "none", cursor: "pointer", color: "#DC2626", display: "inline-flex" }}><Trash size={15} weight="bold" /></button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ borderTop: "1px solid #E2E8F0", padding: "8px 10px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <button data-testid="fa-form-add-line" onClick={addLine} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px dashed #94A3B8", background: "#fff", color: "#334155", borderRadius: 8, fontSize: 12.5, fontWeight: 700, padding: "7px 12px", cursor: "pointer" }}>
                    <Plus size={14} weight="bold" /> Add receipt line
                  </button>
                  {canWriteOff && (
                    <button data-testid="fa-form-writeoff-residual" onClick={writeOffResidual}
                      title="Add an Adjustment for the leftover residual so the invoice settles"
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #16A34A", background: "#F0FDF4", color: "#15803D", borderRadius: 8, fontSize: 12.5, fontWeight: 700, padding: "7px 12px", cursor: "pointer" }}>
                      <Plus size={14} weight="bold" /> Write off {money(residualNow, inv.currency)} &amp; settle
                    </button>
                  )}
                </div>
              </div>

              {/* Batch roll-up */}
              <div style={{ display: "flex", gap: 20, alignItems: "center", padding: "12px 16px", background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 12.5 }}>Batch Received: <strong data-testid="fa-form-batch-recd">{money(batchReceived, inv.currency)}</strong></div>
                <div style={{ fontSize: 12.5 }}>Batch INR: <strong data-testid="fa-form-batch-inr">{inr(batchInr)}</strong></div>
                <div style={{ fontSize: 12.5 }}>Batch Gain / Loss: <strong data-testid="fa-form-batch-gain" style={{ color: batchGain >= 0 ? "#16A34A" : "#DC2626" }}>{inr(batchGain)}</strong></div>
                <div style={{ fontSize: 12.5, marginLeft: "auto" }}>Dues after this batch: <strong data-testid="fa-form-dues-after" style={{ color: outstandingAfter > 0.01 ? "#B45309" : "#16A34A" }}>{money(outstandingAfter, inv.currency)}</strong></div>
              </div>
            </>
            );
          })()}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid #E2E8F0" }}>
          <div style={{ marginRight: "auto", fontSize: 12, fontWeight: 700, color: "#B45309", alignSelf: "center", display: "flex", alignItems: "center", gap: 6 }}><MagicWand size={13} weight="bold" /> Posts a real receipt voucher straight into the ERP — cannot be undone here.</div>
          <button onClick={onClose} style={{ padding: "9px 16px", border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff", color: "#475569", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button data-testid="fa-form-save" onClick={save} disabled={saving || !inv} style={{ padding: "9px 20px", border: "none", borderRadius: 8, background: !inv ? "#94A3B8" : "#16A34A", color: "#fff", fontWeight: 700, fontSize: 13, cursor: !inv ? "not-allowed" : "pointer" }}>{saving ? "Saving…" : "Save Receipts"}</button>
        </div>
      </div>
    </div>
  );
}

export default function FALogistics() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cust, setCust] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");   // all | settled | partial | open
  const [typeFilter, setTypeFilter] = useState("all");        // all | export | domestic
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);                 // user-changeable: 20 | 30 | 50
  const [expanded, setExpanded] = useState(null);             // sale_no of expanded row
  const [downloading, setDownloading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formInvoice, setFormInvoice] = useState(null);       // pre-selected invoice when opened from a row
  const [receiptReload, setReceiptReload] = useState(0);
  const [totalOutInr, setTotalOutInr] = useState(null);
  const [scopedOutInr, setScopedOutInr] = useState(null);     // ERP netted balance AS ON dateTo
  const [scopedOutLoading, setScopedOutLoading] = useState(false);
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [syncedAt, setSyncedAt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [floor, setFloor] = useState("");
  const [floorDraft, setFloorDraft] = useState("");
  const [savingFloor, setSavingFloor] = useState(false);

  useEffect(() => {
    api.get("/fa-logistics/config")
      .then((r) => { const f = r.data?.outstanding_floor || ""; setFloor(f); setFloorDraft(f); })
      .catch(() => {});
  }, []);

  const saveFloor = useCallback(async () => {
    if (!floorDraft || floorDraft === floor) return;
    setSavingFloor(true);
    try {
      const r = await api.put("/fa-logistics/config", { outstanding_floor: floorDraft });
      const f = r.data?.outstanding_floor || floorDraft;
      setFloor(f); setFloorDraft(f);
      toast.success(`Outstanding cutoff set to ${f}. Click "Sync from ERP" to apply it.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save cutoff date"); }
    finally { setSavingFloor(false); }
  }, [floorDraft, floor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/fa-logistics");
      setRows(r.data?.rows || []); setTotalOutInr(r.data?.total_outstanding_inr ?? null);
      setSyncedAt(r.data?.synced_at || null);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Patch a single invoice's listing row in place (Outstanding / Status / received / gain-loss) from
  // the freshly self-healed roll-up returned by the /receipts endpoint. Keeps the cached list from
  // showing a stale value after the modal/timeline has already refreshed that invoice from live ERP.
  const patchRow = useCallback((freshRow) => {
    if (!freshRow || freshRow.sale_no == null) return;
    setRows((prev) => prev.map((x) => (String(x.sale_no) === String(freshRow.sale_no) ? { ...x, ...freshRow } : x)));
  }, []);

  // "Sync from ERP" — kicks off the read-only ERP->Mongo reconcile in the background, polls until
  // done, then reloads the (now-fresh) mirror. Needed only for rare direct-ERP entries; day-to-day
  // the page is already live via write-through when receipts are posted from the app.
  const syncFromErp = useCallback(async () => {
    setSyncing(true);
    try {
      await api.post("/fa-logistics/reconcile");
      toast.info("Syncing from ERP — this can take up to ~2 minutes.");
      for (let i = 0; i < 30; i++) {
        await new Promise((res) => setTimeout(res, 5000));
        const s = await api.get("/fa-logistics/reconcile/status");
        if (!s.data?.running) {
          if (s.data?.last_error) toast.error("ERP sync failed: " + s.data.last_error);
          else toast.success("Synced from ERP.");
          await load();
          break;
        }
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  }, [load]);
  useEffect(() => { setPage(1); }, [q, cust, dateFrom, dateTo, statusFilter, typeFilter, perPage]);

  // Total Outstanding tile with a to-date: fetch the company balance AS ON dateTo from the ERP's own
  // trial-balance proc (identical netted basis as the headline), so dateTo=today reproduces the
  // headline and an earlier date shows the smaller as-on balance. Debounced (the proc is heavy) and
  // request-guarded so a stale slow response can't overwrite a newer date. No dateTo => clear (the
  // tile falls back to the full-today anchor `totalOutInr`).
  useEffect(() => {
    if (!dateTo) { setScopedOutInr(null); setScopedOutLoading(false); return; }
    // to-date today-or-later == the full current balance: use the cached anchor instantly (the heavy
    // ERP trial-balance proc is only needed for a PAST as-on date).
    const todayISO = new Date().toISOString().slice(0, 10);
    if (dateTo >= todayISO) { setScopedOutInr(totalOutInr); setScopedOutLoading(false); return; }
    let cancelled = false;
    setScopedOutLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/fa-logistics/outstanding-as-of", { params: { as_of: dateTo }, timeout: 180000 });
        if (!cancelled) setScopedOutInr(r.data?.total_outstanding_inr ?? null);
      } catch {
        if (!cancelled) setScopedOutInr(null);
      } finally {
        if (!cancelled) setScopedOutLoading(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [dateTo, totalOutInr]);

  // Quick date presets (India FY = Apr 1 → Mar 31).
  const applyQuickRange = (key) => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (key === "this-month") { setDateFrom(iso(new Date(now.getFullYear(), now.getMonth(), 1))); setDateTo(iso(new Date(now.getFullYear(), now.getMonth() + 1, 0))); }
    else if (key === "last-month") { setDateFrom(iso(new Date(now.getFullYear(), now.getMonth() - 1, 1))); setDateTo(iso(new Date(now.getFullYear(), now.getMonth(), 0))); }
    else if (key === "this-fy") { const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; setDateFrom(`${y}-04-01`); setDateTo(`${y + 1}-03-31`); }
    else { setDateFrom(""); setDateTo(""); }
  };

  const openFormForRow = (r) => {
    setFormInvoice({
      sale_no: r.sale_no, invoice_no: r.invoice_no, export_invoice_no: r.export_invoice_no,
      party: r.party, currency: r.currency, sale_date: r.invoice_date, bill_exch_rate: r.sale_rate,
      value_fc: r.value_fc, pcode: r.pcode, compcode: r.compcode || "", is_export: r.is_export,
      voc: r.doc_type === "debit_note" ? "DB Note" : "Bill", sale_noc: r.sale_noc || "",
    });
    setShowForm(true);
  };

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const r = await api.post("/fa-logistics/export.xlsx", { rows: filtered }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `fa_logistics_realization_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Excel downloaded (${filtered.length} rows)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    finally { setDownloading(false); }
  };

  const parties = Array.from(new Set(rows.map((r) => r.party).filter(Boolean))).sort();

  const base = rows.filter((r) => {
    const isDbn = r.doc_type === "debit_note";
    if (typeFilter === "debit_notes") { if (!isDbn) return false; }
    else if (typeFilter === "export") { if (isDbn || !r.is_export) return false; }
    else if (typeFilter === "domestic") { if (isDbn || r.is_export) return false; }
    // typeFilter === "all" includes invoices AND debit notes
    if (cust && !(r.party || "").toLowerCase().includes(cust.toLowerCase())) return false;
    if (q) {
      const t = q.toLowerCase();
      if (!((r.invoice_no || "").toLowerCase().includes(t) || (r.export_invoice_no || "").toLowerCase().includes(t) || (r.party || "").toLowerCase().includes(t))) return false;
    }
    const d = r.invoice_date || "";
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });

  // KPI tiles are INVOICE-ONLY (debit notes appear in the table but never inflate the tiles).
  // Computed independently of the type filter so the tiles stay stable when viewing Debit Notes.
  const invoiceStatsRows = rows.filter((r) => {
    if (r.doc_type === "debit_note") return false;
    if (cust && !(r.party || "").toLowerCase().includes(cust.toLowerCase())) return false;
    if (q) {
      const t = q.toLowerCase();
      if (!((r.invoice_no || "").toLowerCase().includes(t) || (r.export_invoice_no || "").toLowerCase().includes(t) || (r.party || "").toLowerCase().includes(t))) return false;
    }
    const d = r.invoice_date || "";
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });

  const stats = invoiceStatsRows.reduce((a, r) => {
    a.total++;
    a[r.status] = (a[r.status] || 0) + 1;
    if (r.is_export) a.expValUSD += numf(r.value_fc);
    else a.domValINR += numf(r.tinr_amt);
    // Outstanding floors at ZERO per invoice: an over-received invoice (receipts > value, e.g. a
    // receipt mis-mapped to the wrong doc) contributes 0, never a negative that cancels real dues.
    const oFc = Math.max(0, numf(r.outstanding_fc));
    a.outUSD += r.is_export ? oFc : 0;
    // Outstanding in INR: domestic outstanding_fc is ALREADY INR (do NOT multiply by sale_rate —
    // some domestic invoices carry a non-1 rate); only export (FC) is converted at the bill rate.
    a.outINR += r.is_export ? oFc * (numf(r.sale_rate) || 1) : oFc;
    a.gl += numf(r.gain_loss_inr);
    return a;
  }, { total: 0, settled: 0, partial: 0, open: 0, expValUSD: 0, domValINR: 0, outUSD: 0, outINR: 0, gl: 0 });

  const filtered = statusFilter === "all" ? base : base.filter((r) => r.status === statusFilter);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const curPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((curPage - 1) * perPage, curPage * perPage);
  const filtersActive = q || cust || dateFrom || dateTo || statusFilter !== "all" || typeFilter !== "all";
  const clearFilters = () => { setQ(""); setCust(""); setDateFrom(""); setDateTo(""); setStatusFilter("all"); setTypeFilter("all"); };

  // Status-tile counts reflect the TABLE (base) — debit notes INCLUDED — so a count never reads 0
  // while the filter still surfaces open rows (e.g. open debit notes). Money tiles stay invoice-only.
  const statusCounts = base.reduce((a, r) => { a.total++; a[r.status] = (a[r.status] || 0) + 1; return a; },
    { total: 0, open: 0, settled: 0, partial: 0 });

  // One-click preset: open items, all customers, sale date on/after Jan 1 of the current calendar year.
  const curYearStart = `${new Date().getFullYear()}-01-01`;
  const presetActive = !q && !cust && typeFilter === "all" && statusFilter === "open"
    && dateFrom === curYearStart && !dateTo;
  const applyOpenAllSince = () => {
    setQ(""); setCust(""); setTypeFilter("all"); setStatusFilter("open");
    setDateFrom(curYearStart); setDateTo("");
  };
  const curYearLabel = new Date(curYearStart).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <Layout>
      <div data-testid="fa-page" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>FA - Logistics · Export &amp; Domestic Realization</h1>
            <p style={{ fontSize: 13, color: "#64748B", margin: "4px 0 0" }}>Every commercial invoice with its customer receipts, exchange gain/loss and settlement status — served instantly from a synced copy. Receipts you post here write straight to the ERP and update live; use <b>Sync from ERP</b> to pull any entries made directly in the ERP.{syncedAt ? ` · ERP synced ${new Date(syncedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}</p>
          </div>
          <button data-testid="fa-sync-erp" onClick={syncFromErp} disabled={syncing} title="Pull the latest from the ERP (for the rare receipt entered directly in the ERP). Idempotent — never double-counts." style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1px solid #BAE6FD", borderRadius: 8, background: "#F0F9FF", color: "#0369A1", fontWeight: 700, fontSize: 12.5, cursor: syncing ? "wait" : "pointer" }}>
            <ArrowClockwise size={15} weight="bold" style={syncing ? { animation: "spin 1s linear infinite" } : undefined} /> {syncing ? "Syncing from ERP…" : "Sync from ERP"}
          </button>
          <button data-testid="fa-refresh" onClick={load} disabled={loading} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff", color: "#334155", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
            <ArrowClockwise size={15} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button data-testid="fa-add-receipt" onClick={() => setShowForm(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 16px", border: "none", borderRadius: 8, background: "#0F172A", color: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
            <Plus size={15} weight="bold" /> Record Receipt
          </button>
        </div>

        {isAdmin && (
          <div data-testid="fa-floor-setting" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 14px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: "#334155" }}>Show still-outstanding invoices & debit notes dated on/after</span>
            <input data-testid="fa-floor-input" type="date" value={floorDraft} onChange={(e) => setFloorDraft(e.target.value)} style={{ padding: "7px 10px", border: "1px solid #CBD5E1", borderRadius: 7, fontSize: 13 }} />
            <button data-testid="fa-floor-save" onClick={saveFloor} disabled={savingFloor || !floorDraft || floorDraft === floor} style={{ padding: "7px 14px", border: "none", borderRadius: 7, background: (savingFloor || !floorDraft || floorDraft === floor) ? "#94A3B8" : "#0F172A", color: "#fff", fontWeight: 700, fontSize: 12.5, cursor: (savingFloor || !floorDraft || floorDraft === floor) ? "default" : "pointer" }}>{savingFloor ? "Saving…" : "Save"}</button>
            <span style={{ fontSize: 11.5, color: "#64748B" }}>Takes effect after the next <b>Sync from ERP</b>. Current-FY invoices always show.</span>
          </div>
        )}

        {/* KPI tiles */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Tile testid="fa-tile-all" label="Invoices" value={statusCounts.total.toLocaleString("en-IN")} sub={filtersActive ? "in filter" : "all"} color="#0F172A" active={statusFilter === "all"} onClick={() => setStatusFilter("all")} />
          <Tile testid="fa-tile-settled" label="Settled" value={(statusCounts.settled || 0).toLocaleString("en-IN")} sub="click to filter" color="#16A34A" active={statusFilter === "settled"} onClick={() => setStatusFilter("settled")} />
          <Tile testid="fa-tile-partial" label="Partial" value={(statusCounts.partial || 0).toLocaleString("en-IN")} sub="click to filter" color="#B45309" active={statusFilter === "partial"} onClick={() => setStatusFilter("partial")} />
          <Tile testid="fa-tile-open" label="Open" value={(statusCounts.open || 0).toLocaleString("en-IN")} sub="click to filter" color="#DC2626" active={statusFilter === "open"} onClick={() => setStatusFilter("open")} />
          <Tile testid="fa-tile-value" label="Export Receipts" value={"$" + Math.round(stats.expValUSD).toLocaleString("en-US")} sub="USD (export)" color="#0369A1" />
          <Tile testid="fa-tile-dom-value" label="Domestic Receipts" value={inr(stats.domValINR)} sub="INR (domestic)" color="#0E7490" />
          <Tile testid="fa-tile-total-out-inr" label="Total Outstanding (INR)" value={dateTo ? (scopedOutLoading ? "…" : (scopedOutInr == null ? "—" : inr(scopedOutInr))) : (totalOutInr == null ? "—" : inr(totalOutInr))} sub={dateTo ? `INR · balance as on ${new Date(dateTo).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} · ERP netted · view ↗` : "INR · full ERP ledger balance · matches ERP screen · view ↗"} color="#DC2626" onClick={() => setShowOutstanding(true)} />
          <Tile testid="fa-tile-out" label="Outstanding" value={"$" + Math.round(stats.outUSD).toLocaleString("en-US")} sub="USD (export)" color="#7C3AED" />
          <Tile testid="fa-tile-gl" label="Net Gain / Loss" value={inr(stats.gl)} sub="INR" color={stats.gl >= 0 ? "#16A34A" : "#DC2626"} />
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative" }}>
            <MagnifyingGlass size={15} weight="bold" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94A3B8" }} />
            <input data-testid="fa-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search GST / export invoice # or party…" style={{ padding: "9px 12px 9px 32px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, width: 280 }} />
          </div>
          <input data-testid="fa-cust-filter" list="fa-parties" value={cust} onChange={(e) => setCust(e.target.value)} placeholder="Filter by customer…" style={{ padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, width: 200 }} />
          <datalist id="fa-parties">{parties.map((p) => <option key={p} value={p} />)}</datalist>

          <select data-testid="fa-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}>
            <option value="all">All types</option>
            <option value="export">Export</option>
            <option value="domestic">Domestic</option>
            <option value="debit_notes">Debit Notes</option>
          </select>

          <label style={{ fontSize: 12, color: "#64748B", fontWeight: 700 }}>From</label>
          <input data-testid="fa-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 12.5 }} />
          <select data-testid="fa-date-preset" onChange={(e) => { applyQuickRange(e.target.value); e.target.selectedIndex = 0; }} defaultValue="" style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 12.5, color: "#475569", fontWeight: 700 }} title="Quick date range">
            <option value="" disabled>Quick range…</option>
            <option value="this-month">This month</option>
            <option value="last-month">Last month</option>
            <option value="this-fy">This FY (Apr–Mar)</option>
            <option value="clear">Clear dates</option>
          </select>
          <label style={{ fontSize: 12, color: "#64748B", fontWeight: 700 }}>To</label>
          <input data-testid="fa-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 12.5 }} />

          <button data-testid="fa-preset-open-all" onClick={applyOpenAllSince} title={`Show OPEN items for all customers with sale date on/after ${curYearLabel}`}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 8, border: `1px solid ${presetActive ? "#DC2626" : "#CBD5E1"}`, background: presetActive ? "#FEE2E2" : "#fff", color: presetActive ? "#991B1B" : "#475569", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
            Open · all customers · since {curYearLabel}
          </button>

          {filtersActive && (
            <button data-testid="fa-clear-filters" onClick={clearFilters} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, background: "#fff", color: "#475569", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}><X size={13} weight="bold" /> Clear</button>
          )}
          <button data-testid="fa-download-xlsx" onClick={downloadXlsx} disabled={downloading || rows.length === 0} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 16px", border: "1px solid #16A34A", borderRadius: 8, background: "#fff", color: "#16A34A", fontWeight: 700, fontSize: 13, cursor: rows.length === 0 ? "not-allowed" : "pointer", opacity: rows.length === 0 ? 0.5 : 1 }}>
            <DownloadSimple size={15} weight="bold" /> {downloading ? "Exporting…" : "Download XLSX"}
          </button>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="fa-table" style={{ borderCollapse: "collapse", width: "100%", minWidth: 1150 }}>
              <thead>
                <tr style={{ background: "#0F172A" }}>
                  <th style={{ ...th, width: 34 }}></th>
                  <th style={th}>Invoice #</th>
                  <th style={th}>Type</th>
                  <th style={th}>Plant</th>
                  <th style={th}>Party</th>
                  <th style={th}>Inv Date</th>
                  <th style={{ ...th, textAlign: "right" }}>Sale Rate</th>
                  <th style={{ ...th, textAlign: "right" }}>Invoice Value</th>
                  <th style={{ ...th, textAlign: "right" }}>Received</th>
                  <th style={{ ...th, textAlign: "right" }} title="Government incentives received against this invoice (Drawback / GST refund / RODTEP), in INR">Incentives Recd (INR)</th>
                  <th style={{ ...th, textAlign: "right" }}>Outstanding</th>
                  <th style={{ ...th, textAlign: "right" }}>Gain/Loss (INR)</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={13} style={{ ...td, textAlign: "center", padding: 40, color: "#94A3B8" }}>Loading from ERP…</td></tr>}
                {!loading && pageRows.map((r) => {
                  const rk = r.row_key || r.sale_no;
                  const open = expanded === rk;
                  return (
                    <Fragment key={rk}>
                      <tr key={rk} data-testid={`fa-row-${r.invoice_no}`} onClick={() => openFormForRow(r)} title="Click to record a receipt for this invoice" style={{ cursor: "pointer", background: open ? "#F8FAFC" : "#fff" }}>
                        <td onClick={(e) => { e.stopPropagation(); setExpanded(open ? null : rk); }} data-testid={`fa-expand-${r.invoice_no}`} title="Show receipt timeline" style={{ ...td, textAlign: "center", color: "#64748B", cursor: "pointer" }}>{open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}</td>
                        <td style={{ ...td, fontWeight: 700 }}>
                          <div data-testid={`fa-invno-${r.invoice_no}`}>{r.invoice_no}</div>
                          {r.export_invoice_no && (
                            <button
                              data-testid={`fa-expno-${r.export_invoice_no}`}
                              title="Filter by this export invoice #"
                              onClick={(e) => { e.stopPropagation(); setQ(String(r.export_invoice_no)); }}
                              style={{ border: "none", background: "none", padding: 0, cursor: "pointer", fontSize: 10, color: "#0EA5E9", fontWeight: 600, textDecoration: "underline dotted" }}
                            >exp {r.export_invoice_no}</button>
                          )}
                        </td>
                        <td style={td}><TypeBadge exp={r.is_export} dbn={r.doc_type === "debit_note"} /></td>
                        <td style={{ ...td, fontWeight: 700 }}>{r.plant_code || "—"}</td>
                        <td style={{ ...td, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }}>{r.party}</td>
                        <td style={td}>{fmtD(r.invoice_date)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{r.sale_rate ? numf(r.sale_rate).toFixed(2) : "—"}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{money(r.value_fc, r.currency)}</td>
                        <td style={{ ...td, textAlign: "right" }}>{money(r.received_fc, r.currency)}</td>
                        <td style={{ ...td, textAlign: "right", fontSize: 11 }} data-testid={`fa-incentives-${r.invoice_no}`}>
                          {(() => {
                            const dbk = numf(r.drawback), gst = numf(r.gst_received), rod = numf(r.incentive);
                            if (!(dbk || gst || rod)) return <span style={{ color: "#94A3B8" }}>—</span>;
                            return (
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1, lineHeight: 1.25 }}>
                                {dbk > 0 && <span style={{ color: "#C2410C", fontWeight: 700 }}>DBK {inr(dbk)}</span>}
                                {gst > 0 && <span style={{ color: "#1D4ED8", fontWeight: 700 }}>GST {inr(gst)}</span>}
                                {rod > 0 && <span style={{ color: "#0E7490", fontWeight: 700 }}>ROD {inr(rod)}</span>}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ ...td, textAlign: "right", color: numf(r.outstanding_fc) > 0.01 ? "#B45309" : "#64748B" }}>{money(r.outstanding_fc, r.currency)}</td>
                        <td style={{ ...td, textAlign: "right", fontWeight: 800, color: numf(r.gain_loss_inr) >= 0 ? "#16A34A" : "#DC2626" }}>{r.gain_loss_inr ? inr(r.gain_loss_inr) : "—"}</td>
                        <td style={td}><StatusBadge s={r.status} /></td>
                      </tr>
                      {open && (
                        <tr key={`${r.sale_no}-x`} data-testid={`fa-receipts-${r.invoice_no}`}>
                          <td colSpan={13} style={{ padding: 0, background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
                            <ReceiptTimeline saleNo={r.sale_no} voc={r.doc_type === "debit_note" ? "DB Note" : ""} compcode={r.compcode || ""} pcode={r.pcode || ""} saleNoc={r.sale_noc || ""} reloadKey={receiptReload} onChanged={load} onRowRefreshed={r.doc_type === "debit_note" ? undefined : patchRow} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {!loading && filtered.length === 0 && <tr><td colSpan={13} style={{ ...td, textAlign: "center", padding: 40, color: "#94A3B8" }}>No invoices match the current filters.</td></tr>}
              </tbody>
            </table>
          </div>
          {!loading && filtered.length > 0 && (
            <div data-testid="fa-pagination" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderTop: "1px solid #E2E8F0", flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#64748B" }}>
                <span>Showing <strong>{(curPage - 1) * perPage + 1}–{Math.min(curPage * perPage, filtered.length)}</strong> of <strong>{filtered.length}</strong> invoices{filtersActive ? " (filtered)" : ""}</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontWeight: 700 }}>Rows</span>
                  <select data-testid="fa-page-size" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} style={{ padding: "5px 8px", border: "1px solid #CBD5E1", borderRadius: 7, fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </select>
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button data-testid="fa-page-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage <= 1} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", borderRadius: 7, background: "#fff", fontSize: 12.5, fontWeight: 700, color: curPage <= 1 ? "#CBD5E1" : "#0F172A", cursor: curPage <= 1 ? "not-allowed" : "pointer" }}>Prev</button>
                <span data-testid="fa-page-indicator" style={{ fontSize: 12.5, fontWeight: 700, color: "#334155", minWidth: 90, textAlign: "center" }}>Page {curPage} of {totalPages}</span>
                <button data-testid="fa-page-next" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={curPage >= totalPages} style={{ padding: "6px 12px", border: "1px solid #CBD5E1", borderRadius: 7, background: "#fff", fontSize: 12.5, fontWeight: 700, color: curPage >= totalPages ? "#CBD5E1" : "#0F172A", cursor: curPage >= totalPages ? "not-allowed" : "pointer" }}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showForm && (
        <ReceiptForm
          initialInvoice={formInvoice}
          onClose={() => { setShowForm(false); setFormInvoice(null); }}
          onInvoiceRefreshed={patchRow}
          onSaved={(saleNo, invNo) => { load(); setReceiptReload((k) => k + 1); if (invNo) { setQ(invNo); setStatusFilter("all"); setTypeFilter("all"); } if (saleNo) setExpanded(saleNo); }}
        />
      )}
      {showOutstanding && <OutstandingModal onClose={() => setShowOutstanding(false)} />}
    </Layout>
  );
}
